"use client";

import {
  FragmentOfShopifyStorefront,
  ResultOfShopifyStorefront,
  VariablesOfShopifyStorefront,
} from "@repo/graphql-shopify-storefront";
import {
  useMutation,
  useQueryClient,
  type DefaultError,
} from "@tanstack/react-query";
import { getCookie } from "cookies-next/client";
import { useSearchParams } from "next/navigation";
import { Suspense, useCallback } from "react";
import { toast } from "sonner";
import { useAnalytics } from "use-analytics";
import {
  Cart_Fragment,
  // Image_Fragment,
  ProductOption_Fragment,
  SellingPlanGroup_Fragment,
} from "#/graphql/shopify-storefront/fragments";
import {
  CartLinesAdd_Mutation,
  CartCreate_Mutation,
} from "#/graphql/shopify-storefront/mutations/cart";
import { Cart_Query } from "#/graphql/shopify-storefront/queries";
import {
  fetcherShopifyStorefront,
  getInContextVariables,
  readFragmentShopifyStorefront,
} from "@repo/graphql-shopify-storefront";
import type { CartLineInput } from "@shopify/hydrogen-react/storefront-api-types";
import type { Usable } from "react";
import type { ResolvedIntlConfig } from "react-intl";
import { RxCheck, RxClock, RxPlus } from "react-icons/rx";
import { Button } from "@repo/components/atoms/shadcn/button";
import { LoadingDots } from "@repo/components/atoms/loading-dots";
import { useGetIntl } from "@repo/i18n/intl/client";
import { cn } from "@repo/lib";
import {
  AddToCart_ProductVariant_Fragment,
  AddToCart_ProductVariantConnectionMasked_Fragment,
  AddToCart_Product_Fragment,
} from "./graphql";

type ButtonProps = React.ComponentProps<typeof Button>;

function SubmitButton({
  className,
  container,
  dictionaryPromise,
  lang,
  productMasked,
  selectedVariantId,
  size,
  variant,
}: {
  className?: string;
  container?: string;
  dictionaryPromise: Usable<ResolvedIntlConfig["messages"]>;
  lang: Navigator["language"];
  productMasked: FragmentOfShopifyStorefront<typeof AddToCart_Product_Fragment>;
  selectedVariantId: string | undefined;
  size: ButtonProps["size"];
  variant: ButtonProps["variant"];
}) {
  const intl = useGetIntl({
    dictionaryPromise,
    lang,
  });

  const shopifyQueryClient = useQueryClient();

  const { track } = useAnalytics() || {};

  const cartId = getCookie("cartId");

  const customerAccessToken = getCookie("customerAccessToken");

  const email = getCookie("email");

  const { country } = getInContextVariables(lang);

  const product = readFragmentShopifyStorefront(
    AddToCart_Product_Fragment,
    productMasked,
  );

  const { availableForSale, sellingPlanGroupsConnectionMasked } = product;

  const sellingPlanGroups = sellingPlanGroupsConnectionMasked.edges.map(
    ({ node: sellingPlanGroupMasked }) =>
      readFragmentShopifyStorefront(
        SellingPlanGroup_Fragment,
        sellingPlanGroupMasked,
      ),
  );

  const preOrder = sellingPlanGroups.find(({ name }) => name === "Pre-order");

  const sellingPlanId = preOrder?.sellingPlans?.edges?.[0]?.node?.id;

  const CartLinesAddMutationFn = (
    variables: VariablesOfShopifyStorefront<typeof CartLinesAdd_Mutation>,
  ) => {
    return fetcherShopifyStorefront(CartLinesAdd_Mutation, variables);
  };

  const CartCreateMutationFn = (
    variables: VariablesOfShopifyStorefront<typeof CartCreate_Mutation>,
  ) => fetcherShopifyStorefront(CartCreate_Mutation, variables);

  const invalidateQueries = () => {
    if (!cartId) {
      return;
    }

    const queryKey = [Cart_Query, { cartId }];

    // TODO: Check why invalidateQueries call for cart contents is not working
    shopifyQueryClient.invalidateQueries({
      queryKey,
    });
  };

  const onError = <TError extends DefaultError, TVariables>(
    error: TError,
    variables: TVariables,
  ) => {
    console.error("onError", { error, variables });

    toast.error(
      intl.formatMessage({
        defaultMessage: "Unable to add the item to your cart",
        id: "K9mHO4",
        description:
          "Text for an unsuccessful add the item to your cart status title in the cartLinesAdd component",
      }),
      {
        description: intl.formatMessage({
          defaultMessage:
            "The add the item to your cart action failed with the following error: {message}",
          id: "Fk4rvu",
          description:
            "Text for an unsuccessful add the item to your cart status message in the cartLinesAdd component",
        }),
      },
    );
  };

  const onSuccess = <
    TData extends
      | ResultOfShopifyStorefront<typeof CartLinesAdd_Mutation>
      | ResultOfShopifyStorefront<typeof CartCreate_Mutation>,
    TVariables,
  >(
    data: TData,
    variables: TVariables,
  ) => {
    console.debug("onSuccess", { data, variables });

    let cartMasked;

    if ("cartCreate" in data) {
      cartMasked = data.cartCreate?.cartMasked;
    }

    if ("cartLinesAdd" in data) {
      cartMasked = data.cartLinesAdd?.cartMasked;
    }

    const cart = readFragmentShopifyStorefront(Cart_Fragment, cartMasked);

    if (cart?.id) {
      toast.success(
        intl.formatMessage({
          defaultMessage: "You have successfully added the item to your cart",
          id: "iT+Q7v",
          description:
            "Text for a successful add the item to your cart status title in the cartLinesAdd component",
        }),
        {
          description: intl.formatMessage({
            defaultMessage: "You have successfully added the item to your cart",
            id: "/OGGcV",
          }),
          onAutoClose: () => {
            invalidateQueries();
          },
          onDismiss: () => {
            invalidateQueries();
          },
        },
      );
    }
  };

  const {
    isPending: isPendingcartLinesAdd,
    mutate: mutatecartLinesAdd,
    status: statuscartLinesAdd,
  } = useMutation({
    mutationFn: CartLinesAddMutationFn,
    mutationKey: ["cartLinesAdd"],
    onError,
    onSuccess,
  });

  const {
    isPending: isPendingCreateCart,
    mutate: mutateCreateCart,
    status: statusCreateCart,
  } = useMutation({
    mutationFn: CartCreateMutationFn,
    mutationKey: ["createCart"],
    onError,
    onSuccess,
  });

  const isPending = isPendingcartLinesAdd || isPendingCreateCart;

  const isDisabled = isPending || !availableForSale || !selectedVariantId;

  const isSuccess =
    statuscartLinesAdd === "success" || statusCreateCart === "success";

  let label = intl.formatMessage({
    defaultMessage: "Add to Cart",
    description: "Add to Cart",
    id: "IR69np",
  });

  if (!selectedVariantId) {
    label = intl.formatMessage({
      defaultMessage: "Select Options",
      description: "Select Options",
      id: "T8tt6Z",
    });
  }

  if (!availableForSale) {
    label = intl.formatMessage({
      defaultMessage: "Out of Stock",
      description: "Out of Stock",
      id: "z9+Olg",
    });
  }

  if (preOrder) {
    label = intl.formatMessage({
      defaultMessage: "Pre-Order",
      description: "Pre-Order",
      id: "nbQEla",
    });
  }

  if (isPending) {
    label = intl.formatMessage({
      defaultMessage: "Adding to Cart",
      description: "Adding to Cart",
      id: "kGiedU",
    });
  }

  if (isSuccess) {
    label = intl.formatMessage({
      defaultMessage: "Added to Cart",
      description: "Added to Cart",
      id: "O0rQT8",
    });
  }

  const onClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();

      if (isDisabled) {
        return null;
      }

      const payload = {
        merchandiseId: selectedVariantId,
        quantity: 1,
        sellingPlanId,
      } satisfies CartLineInput;

      const cartId = getCookie("cartId");

      if (cartId) {
        mutatecartLinesAdd({
          cartId,
          lines: [payload],
        });
      } else {
        mutateCreateCart({
          input: {
            buyerIdentity: {
              countryCode: country,
              customerAccessToken,
              email,
            },
            lines: [payload],
          },
        });
      }

      if (track) {
        track("add_to_cart", {
          availableForSale,
          container,
          selectedVariantId,
          sellingPlanId,
        });
      }
    },
    [
      isDisabled,
      selectedVariantId,
      sellingPlanId,
      track,
      availableForSale,
      container,
      mutatecartLinesAdd,
      mutateCreateCart,
      country,
      customerAccessToken,
      email,
    ],
  );

  let icon = <RxPlus className="h-5 w-6" />;

  if (isSuccess) {
    icon = <RxCheck className="h-5 w-6" />;
  } else if (isPending) {
    icon = <LoadingDots className="*:bg-accent-foreground" />;
  } else if (preOrder) {
    icon = <RxClock className="h-5 w-6" />;
  }

  return (
    <Button
      aria-label={label}
      aria-disabled={isDisabled}
      className={cn(
        {
          "cursor-not-allowed": isDisabled,
        },
        className,
      )}
      onClick={onClick}
      size={size}
      variant={variant}
    >
      {icon}
      {label}
      <span aria-live="polite" className="sr-only" role="status">
        {isPending &&
          intl.formatMessage({
            defaultMessage: "Adding to Cart",
            description: "Adding to Cart",
            id: "kGiedU",
          })}
        {isSuccess &&
          intl.formatMessage({
            defaultMessage: "Added to Cart",
            description: "Added to Cart",
            id: "O0rQT8",
          })}
      </span>
    </Button>
  );
}

export function AddToCart({
  className,
  container,
  dictionaryPromise,
  lang,
  productMasked,
  size,
  variant,
}: {
  className?: string;
  container?: string;
  dictionaryPromise: Usable<ResolvedIntlConfig["messages"]>;
  lang: Navigator["language"];
  productMasked: FragmentOfShopifyStorefront<typeof AddToCart_Product_Fragment>;
  size?: ButtonProps["size"];
  variant?: ButtonProps["variant"];
}) {
  const searchParams = useSearchParams();

  const product = readFragmentShopifyStorefront(
    AddToCart_Product_Fragment,
    productMasked,
  );

  const { optionsMasked, variantsConnectionMasked } = product;

  const options = optionsMasked.map((optionMasked) =>
    readFragmentShopifyStorefront(ProductOption_Fragment, optionMasked),
  );

  const variantsMasked = readFragmentShopifyStorefront(
    AddToCart_ProductVariantConnectionMasked_Fragment,
    variantsConnectionMasked,
  );

  const selectedVariant = variantsMasked.edges
    .map(({ node: productVariant }) => productVariant)
    .map((productVariant) =>
      readFragmentShopifyStorefront(
        AddToCart_ProductVariant_Fragment,
        productVariant,
      ),
    )
    .find((productVariant) => {
      return productVariant.selectedOptions?.every((selectedOption) => {
        const hasNoOptionsOrJustOneOption =
          options.find((option) => option.name === selectedOption.name)
            ?.optionValues.length === 1;

        if (hasNoOptionsOrJustOneOption) {
          return true;
        }

        const equality =
          selectedOption?.value?.toLowerCase() ===
          searchParams?.get(selectedOption.name.toLowerCase())?.toLowerCase();

        return equality;
      });
    });

  const selectedVariantId = selectedVariant?.id;

  const intl = useGetIntl({
    dictionaryPromise,
    lang,
  });

  return (
    <Suspense
      fallback={
        <Button
          aria-disabled
          aria-label={intl.formatMessage({
            defaultMessage: "Adding to Cart",
            description: "Adding to Cart",
            id: "kGiedU",
          })}
          className={className}
          disabled
          size={size}
          variant={variant}
        >
          <LoadingDots />

          {intl.formatMessage({
            defaultMessage: "Adding to Cart",
            description: "Adding to Cart",
            id: "kGiedU",
          })}
        </Button>
      }
    >
      <SubmitButton
        className={className}
        container={container}
        dictionaryPromise={dictionaryPromise}
        lang={lang}
        productMasked={productMasked}
        selectedVariantId={selectedVariantId}
        size={size}
        variant={variant}
      />
    </Suspense>
  );
}
